import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AgentTestingPage.css';

// Component imports
import Spinner from '../components/Spinner';
import AgentHeader from '../components/testing/AgentHeader';
import AgentInfoCard from '../components/testing/AgentInfoCard';
import InputParametersForm from '../components/testing/InputParametersForm';
import ExecutionStatusCard from '../components/testing/ExecutionStatusCard';
import ExecutionResultCard from '../components/testing/ExecutionResultCard';
import LiveLogsCard from '../components/testing/LiveLogsCard';
import LambdaLogsCard from '../components/testing/LambdaLogsCard';
import AnalysisSection from '../components/testing/AnalysisSection';
import QuickEditSection from '../components/testing/QuickEditSection';

// Utility imports
import {
  getDefaultValueForType,
  initializeNestedObject,
  flattenObjectToFields,
  getNestedValue,
  setNestedValue,
  validateForm,
  cleanFormDataForSubmission
} from '../components/testing/utils/formUtils';

import {
  safeRenderValue,
  extractFromNestedResponse,
  parseLiveLogsOutput,
  formatLiveLogsResponse
} from '../components/testing/utils/logsUtils';

const AgentTestingPage = () => {
  const { agentId, microAgentId, miniAgentId } = useParams();
  const navigate = useNavigate();
  
  // Determine the agent ID based on the URL parameters
  const targetId = microAgentId || miniAgentId;
  const agentType = microAgentId ? 'micro-agent' : 'mini-agent';
  const agentTypeLabel = microAgentId ? 'Micro-Agent' : 'Mini-Agent';
  
  // State variables
  const [agent, setAgent] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executingEnvironment, setExecutingEnvironment] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [executionArn, setExecutionArn] = useState(null);
  const [environment, setEnvironment] = useState('development');
  const [formErrors, setFormErrors] = useState({});
  const [liveLogs, setLiveLogs] = useState(null);
  const [fetchingLogs, setFetchingLogs] = useState(false);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(false);
  
  // Lambda logs state
  const [lambdaLogs, setLambdaLogs] = useState(null);
  const [fetchingLambdaLogs, setFetchingLambdaLogs] = useState(false);
  
  // Polling control state
  const [statusPollingActive, setStatusPollingActive] = useState(true);
  const [logsPollingActive, setLogsPollingActive] = useState(true);
  
  // Execution details state
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loadingExecutionDetails, setLoadingExecutionDetails] = useState(false);
  
  // Parsed execution response fields
  const [bodyMessage, setBodyMessage] = useState(null);
  const [bodyExecutionArn, setBodyExecutionArn] = useState(null);

  // Parsed live logs output fields
  const [parsedExecutionSummary, setParsedExecutionSummary] = useState(null);
  const [parsedAgentOutput, setParsedAgentOutput] = useState(null);

  // Analysis state
  const [showAnalysisSection, setShowAnalysisSection] = useState(false);
  const [fetchingAnalysis, setFetchingAnalysis] = useState(false);
  const [analysisExecutionArn, setAnalysisExecutionArn] = useState(null);
  const [analysisPollingActive, setAnalysisPollingActive] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [fetchingAnalysisStatus, setFetchingAnalysisStatus] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  // Quick Edit state
  const [showQuickEditSection, setShowQuickEditSection] = useState(false);
  const [quickEditInstructions, setQuickEditInstructions] = useState('');
  const [includeLambdaLogs, setIncludeLambdaLogs] = useState(false);
  const [includeExecutionAnalysis, setIncludeExecutionAnalysis] = useState(false);
  const [submittingQuickEdit, setSubmittingQuickEdit] = useState(false);
  const [quickEditResult, setQuickEditResult] = useState(null);
  const [quickEditReferenceAgent, setQuickEditReferenceAgent] = useState('');

  // Data restored from LogsReview state
  const [restoredFromLogs, setRestoredFromLogs] = useState(false);

  // Stop execution state
  const [stoppingExecution, setStoppingExecution] = useState(false);

  // Fetch agent details and schema on load
  useEffect(() => {
    fetchAgentDetails();
    fetchSchema();
  }, [targetId, agentType]);

  // Parse live logs when they update
  useEffect(() => {
    if (liveLogs) {
      const parsed = parseLiveLogsOutput(liveLogs);
      setParsedExecutionSummary(parsed.executionSummary);
      setParsedAgentOutput(parsed.agentOutput);
    }
  }, [liveLogs]);

  // Watch for status changes to stop polling when execution completes
  useEffect(() => {
    let timeoutId = null;
    
    if (executionStatus && (
      executionStatus.status === 'SUCCEEDED' || 
      executionStatus.status === 'FAILED' ||
      executionStatus.status === 'COMPLETED'
    )) {
      if (statusPollingActive || logsPollingActive) {
        console.log(`Execution ${executionStatus.status} - stopping all polling`);
        setStatusPollingActive(false);
        setLogsPollingActive(false);
        
        if (executionArn && executionStatus.status === 'SUCCEEDED') {
          console.log('Execution SUCCEEDED - waiting 10 seconds before retrieving execution details');
          timeoutId = setTimeout(() => {
            console.log('10 second delay completed - now fetching execution details');
            fetchBasicExecutionDetails(executionArn);
          }, 10000);
        }
      }
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [executionStatus, statusPollingActive, logsPollingActive, executionArn]);

  // ENHANCED: Effect to check for saved execution payload and restore it
  useEffect(() => {
    if (formFields.length > 0 && schema) {
      try {
        const savedExecutionJSON = localStorage.getItem('retryExecutionPayload');
        if (savedExecutionJSON) {
          const savedExecution = JSON.parse(savedExecutionJSON);
          console.log('Found saved execution context to restore:', savedExecution);
          
          if (savedExecution.environment) {
            setEnvironment(savedExecution.environment);
            setExecutingEnvironment(savedExecution.environment);
          }

          if (savedExecution.executionArn && savedExecution.fromLogsReview) {
            console.log('Restoring execution context from LogsReview with ARN:', savedExecution.executionArn);
            setRestoredFromLogs(true);
            setExecutionArn(savedExecution.executionArn);
            
            fetchExecutionPayloadAndPopulate(savedExecution.executionArn);
            
            if (savedExecution.executionStatus) {
              setExecutionStatus(savedExecution.executionStatus);
              setStatusPollingActive(false);
              setLogsPollingActive(false);
            }
            
            if (savedExecution.executionResult) {
              setExecutionDetails({
                status: savedExecution.executionResult.outcome || 'COMPLETED',
                message: 'Execution data restored from logs',
                executionArn: savedExecution.executionArn
              });
              setLoadingExecutionDetails(false);
              
              if (savedExecution.executionResult.agentOutput) {
                setParsedAgentOutput(safeRenderValue(savedExecution.executionResult.agentOutput));
              }
              
              if (savedExecution.executionResult.executionLog) {
                setParsedExecutionSummary(safeRenderValue(savedExecution.executionResult.executionLog));
              }
            }
            
            if (savedExecution.liveLogs) {
              setLiveLogs(savedExecution.liveLogs);
              setFetchingLogs(false);
            }
            
            toast.info(`Form and execution context being populated from execution: ${savedExecution.executionId || 'previous execution'}`);
          } else {
            const newFormData = {...formData};
            
            if (savedExecution.payload && typeof savedExecution.payload === 'object') {
              const mergePayload = (target, source) => {
                Object.keys(source).forEach(key => {
                  if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    if (!target[key]) target[key] = {};
                    mergePayload(target[key], source[key]);
                  } else {
                    target[key] = source[key];
                  }
                });
              };
              
              mergePayload(newFormData, savedExecution.payload);
              setFormData(newFormData);
              toast.info('Form populated with previous execution data');
            }
          }
          
          localStorage.removeItem('retryExecutionPayload');
        }
      } catch (err) {
        console.error('Error restoring execution payload:', err);
        toast.error('Failed to restore execution data');
      }
    }
  }, [formFields, schema]);

  // Watch for analysis status changes
  useEffect(() => {
    if (analysisStatus && (
      analysisStatus.status === 'SUCCEEDED' || 
      analysisStatus.status === 'FAILED' ||
      analysisStatus.status === 'COMPLETED'
    )) {
      if (analysisPollingActive) {
        console.log(`Analysis execution ${analysisStatus.status} - stopping analysis polling`);
        setAnalysisPollingActive(false);
        
        if (analysisExecutionArn && analysisStatus.status === 'SUCCEEDED') {
          console.log('Analysis SUCCEEDED - fetching final analysis result');
          setTimeout(() => {
            fetchAnalysisResult(analysisExecutionArn);
          }, 5000);
        }
      }
    }
  }, [analysisStatus, analysisPollingActive, analysisExecutionArn]);

  // Polling setup for execution status and logs
  useEffect(() => {
    if (!executionArn) return;
    
    console.log('Setting up execution monitoring for ARN:', executionArn);
    
    if (!restoredFromLogs) {
      fetchLiveLogs(executionArn);
      fetchExecutionStatus(executionArn);
    } else {
      setTimeout(() => {
        fetchLiveLogs(executionArn);
        fetchExecutionStatus(executionArn);
      }, 1000);
    }
    
    let logsIntervalId, statusIntervalId;
    
    if (logsPollingActive && !restoredFromLogs) {
      logsIntervalId = setInterval(() => {
        fetchLiveLogs(executionArn);
      }, 5000);
    }
    
    if (statusPollingActive && !restoredFromLogs) {
      statusIntervalId = setInterval(() => {
        fetchExecutionStatus(executionArn);
      }, 5000);
    }
    
    return () => {
      if (logsIntervalId) clearInterval(logsIntervalId);
      if (statusIntervalId) clearInterval(statusIntervalId);
    };
  }, [executionArn, agent?.agentNumber, parentAgent, executingEnvironment, statusPollingActive, logsPollingActive, restoredFromLogs]);

  // Analysis polling setup
  useEffect(() => {
    if (!analysisExecutionArn) return;
    
    fetchAnalysisStatus(analysisExecutionArn);
    
    let analysisIntervalId;
    
    if (analysisPollingActive) {
      analysisIntervalId = setInterval(() => {
        fetchAnalysisStatus(analysisExecutionArn);
      }, 5000);
    }
    
    return () => {
      if (analysisIntervalId) clearInterval(analysisIntervalId);
    };
  }, [analysisExecutionArn, agent?.agentNumber, parentAgent, analysisPollingActive]);

  // Fetch execution payload and populate form
  const fetchExecutionPayloadAndPopulate = async (arn) => {
    if (!arn) return;
    
    try {
      console.log(`Fetching full execution details for ARN: ${arn}`);
      
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
        execution_arn: arn
      });
      
      console.log('Raw execution details response:', response);
      
      const executionData = extractFromNestedResponse(response, 'data');
      
      if (executionData && executionData.payload) {
        console.log('Fetched execution data with payload:', executionData.payload);
        
        let payload = executionData.payload;
        
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (e) {
            console.warn('Failed to parse payload string:', e);
          }
        }
        
        if (typeof payload === 'object') {
          setFormData(prev => {
            const newData = { ...prev };
            
            const mergePayload = (target, source) => {
              Object.keys(source).forEach(key => {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                  if (!target[key]) target[key] = {};
                  mergePayload(target[key], source[key]);
                } else {
                  target[key] = source[key];
                }
              });
            };
            
            mergePayload(newData, payload);
            return newData;
          });
          
          toast.success('Form populated from execution history');
          console.log('Successfully populated form with execution payload');
        }
      } else {
        console.warn('No payload found in execution data');
        toast.warning('Execution found but no payload data available');
      }
    } catch (err) {
      console.error('Error fetching execution payload:', err);
      toast.error('Failed to restore execution data: ' + err.message);
    }
  };

  // Fetch basic execution details
  const fetchBasicExecutionDetails = async (executionArn) => {
    if (!executionArn) return;
    
    setLoadingExecutionDetails(true);
    
    try {
      console.log(`Fetching basic execution details for: ${executionArn}`);
      
      const agentNumber = agent?.agentNumber || '';
      if (agentNumber && agentNumber.length === 3) {
        const payload = {
          agentNumber: agentNumber,
          awsAccountNumber: parentAgent?.awsAccount || "851725399969",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          executionArn: executionArn,
          parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
          environment: executingEnvironment
        };
        
        const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/status`, payload);
        
        if (response && response.response && response.response.body) {
          let statusData = response.response.body;
          if (typeof statusData === 'string') {
            try {
              statusData = JSON.parse(statusData);
            } catch (parseError) {
              console.error('Error parsing status response:', parseError);
            }
          }
          
          setExecutionDetails({
            status: statusData?.status || 'COMPLETED',
            message: 'Execution completed successfully',
            executionArn: executionArn
          });
        }
      }
      
    } catch (err) {
      console.error('Error fetching basic execution details:', err);
    } finally {
      setLoadingExecutionDetails(false);
    }
  };

  // Handle analyze execution
  const handleAnalyzeExecution = async () => {
    if (!executionArn) return;

    setFetchingAnalysis(true);
    setAnalysisResult(null);
    setAnalysisStatus(null);
    setShowAnalysisSection(true);

    try {
      console.log('Starting execution analysis for original executionArn:', executionArn);

      const response = await api.testAgent(
        agentId,
        'micro-agent',
        '30',
        { StepFunctionARN: executionArn },
        'production'
      );

      console.log('Analysis execution response:', response);

      let analysisArn = null;
      if (response && response.body && typeof response.body === 'string') {
        try {
          const parsedBody = JSON.parse(response.body);
          analysisArn = parsedBody.executionArn;
        } catch (parseError) {
          console.error('Error parsing analysis response body:', parseError);
        }
      } else if (response && response.body && response.body.executionArn) {
        analysisArn = response.body.executionArn;
      } else if (response && response.executionArn) {
        analysisArn = response.executionArn;
      }

      if (analysisArn) {
        console.log('Analysis execution ARN (from agent 111):', analysisArn);
        setAnalysisExecutionArn(analysisArn);
        setAnalysisPollingActive(true);
        fetchAnalysisStatus(analysisArn);
        toast.success('Analysis execution started successfully');
      } else {
        throw new Error('No execution ARN received from analysis agent');
      }
    
    } catch (err) {
      console.error('Error starting execution analysis:', err);
      setAnalysisResult({
        success: false,
        error: `Failed to start analysis: ${err.message}`
      });
    } finally {
      setFetchingAnalysis(false);
    }
  };

  // Fetch analysis status
  const fetchAnalysisStatus = async (analysisArn) => {
    if (!analysisArn) return;
    
    try {
      setFetchingAnalysisStatus(true);
      
      console.log('Checking analysis execution status for:', analysisArn);
      
      const payload = {
        agentNumber: "111",
        awsAccountNumber: "606639293792",
        awsRegion: "us-west-2",
        executionArn: analysisArn,
        parentAgentName: "AgentDeveloper",
        environment: "production"
      };
      
      const response = await api.makeAuthenticatedRequest('POST', `/agents/111/status`, payload);
      
      if (response && response.response && response.response.body) {
        if (typeof response.response.body === 'string') {
          try {
            const statusData = JSON.parse(response.response.body);
            setAnalysisStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing analysis status response body:', parseError);
            setAnalysisStatus(response.response.body);
          }
        } else {
          setAnalysisStatus(response.response.body);
        }
      } else if (response && response.body) {
        if (typeof response.body === 'string') {
          try {
            const statusData = JSON.parse(response.body);
            setAnalysisStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing analysis status response body:', parseError);
            setAnalysisStatus(response.body);
          }
        } else {
          setAnalysisStatus(response.body);
        }
      } else {
        console.warn('Unexpected analysis status response format:', response);
        setAnalysisStatus(null);
      }
      
      setFetchingAnalysisStatus(false);
    } catch (err) {
      console.error('Error fetching analysis execution status:', err);
      setFetchingAnalysisStatus(false);
    }
  };

  // Fetch analysis result
  const fetchAnalysisResult = async (analysisArn) => {
    if (!analysisArn) return;

    try {
      console.log('Fetching final analysis result for:', analysisArn);

      const payload = {
        agentNumber: "111",
        awsAccountNumber: "606639293792",
        awsRegion: "us-west-2", 
        executionArn: analysisArn,
        parentAgentName: "AgentDeveloper",
        environment: "production"
      };

      const response = await api.makeAuthenticatedRequest('POST', `/agents/111/log`, payload);

      console.log('Analysis logs response:', response);

      let logsData = null;
      
      if (response && response.response && response.response.body) {
        let responseBody = response.response.body;
        
        if (typeof responseBody === 'string') {
          try {
            responseBody = JSON.parse(responseBody);
          } catch (parseError) {
            console.error('Error parsing analysis logs response body:', parseError);
          }
        }
        
        logsData = responseBody;
      } else if (response && response.body) {
        if (typeof response.body === 'string') {
          try {
            logsData = JSON.parse(response.body);
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
            logsData = response.body;
          }
        } else {
          logsData = response.body;
        }
      } else if (response && response.logs) {
        logsData = response.logs;
      }

      console.log('Analysis logs data:', logsData);

      let agentOutput = null;
      let executionSummary = null;
      
      if (logsData) {
        if (logsData.output && logsData.output.agentOutput && logsData.output.executionSummary) {
          agentOutput = logsData.output.agentOutput;
          executionSummary = logsData.output.executionSummary;
        } 
        else if (logsData.Output && logsData.Output.agentOutput && logsData.Output.executionSummary) {
          agentOutput = logsData.Output.agentOutput;
          executionSummary = logsData.Output.executionSummary;
        }
        else if (logsData.agentOutput && logsData.executionSummary) {
          agentOutput = logsData.agentOutput;
          executionSummary = logsData.executionSummary;
        }
      }

      if (agentOutput && executionSummary) {
        const safeAgentOutput = safeRenderValue(agentOutput);
        const safeExecutionSummary = safeRenderValue(executionSummary);
        
        setAnalysisResult({
          success: true,
          agentOutput: safeAgentOutput,
          executionSummary: safeExecutionSummary
        });
        console.log('Analysis completed successfully with both output fields');
      } else if (agentOutput || executionSummary) {
        const safeAgentOutput = agentOutput ? safeRenderValue(agentOutput) : null;
        const safeExecutionSummary = executionSummary ? safeRenderValue(executionSummary) : null;
        
        setAnalysisResult({
          success: true,
          agentOutput: safeAgentOutput,
          executionSummary: safeExecutionSummary
        });
        console.log('Analysis completed with partial results');
      } else {
        console.warn('Analysis completed but neither agentOutput nor executionSummary found');
        setAnalysisResult({
          success: false,
          error: 'Analysis completed but no output fields found in the expected structure'
        });
      }

    } catch (err) {
      console.error('Error fetching analysis result:', err);
      setAnalysisResult({
        success: false,
        error: `Failed to fetch analysis result: ${err.message}`
      });
    }
  };

  // Handle Quick Edit submission
  const handleQuickEditSubmit = async () => {
    if (!quickEditInstructions.trim() || quickEditInstructions.trim().length < 10) {
      toast.error('Instructions must be at least 10 characters long');
      return;
    }

    // Validate reference agent if provided
    if (quickEditReferenceAgent.trim() && !/^[0-9]{3}$/.test(quickEditReferenceAgent.trim())) {
      toast.error('Reference Agent must be a 3-digit number (e.g., 101, 205)');
      return;
    }

    if (!agentId || !parentAgent) {
      toast.error('Missing required agent or parent agent data');
      return;
    }

    try {
      setSubmittingQuickEdit(true);
      setQuickEditResult(null);

      let changeInstructions = quickEditInstructions.trim();

      // Add lambda logs to context if requested
      if (includeLambdaLogs && lambdaLogs) {
        changeInstructions += '\n\nLambda Logs Context:\n';
        changeInstructions += typeof lambdaLogs === 'string' 
          ? lambdaLogs 
          : JSON.stringify(lambdaLogs, null, 2);
      }

      // Add BOTH agentOutput AND executionSummary to context if execution analysis is included
      if (includeExecutionAnalysis && analysisResult) {
        changeInstructions += '\n\nExecution Analysis Context:\n';
        
        if (analysisResult.agentOutput) {
          changeInstructions += '\n=== Agent Output ===\n';
          changeInstructions += safeRenderValue(analysisResult.agentOutput);
        }
        
        if (analysisResult.executionSummary) {
          changeInstructions += '\n\n=== Execution Summary ===\n';
          changeInstructions += safeRenderValue(analysisResult.executionSummary);
        }
      }

      const githubAccount = parentAgent.repoAccount || parentAgent.github_account;
      const githubRepo = parentAgent.repoName || parentAgent.github_repo;
      
      if (!githubAccount || !githubRepo) {
        throw new Error('GitHub repository information not available in parent agent metadata');
      }

      const githubRepoString = `${githubAccount}/${githubRepo}`;

      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1",
        micro_agent_id: targetId
      });
      
      const microAgents = extractFromNestedResponse(response, 'data', 'micro_agents');
      
      if (!microAgents || !Array.isArray(microAgents) || microAgents.length === 0) {
        throw new Error('No micro-agents found in response');
      }
      
      const currentMicroAgent = microAgents[0];

      if (!currentMicroAgent) {
        throw new Error(`Current micro-agent with ID ${targetId} not found`);
      }

      const currentMicroAgentNumber = currentMicroAgent.micro_agent_number;

      const allAgentsResponse = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1"
      });

      const allMicroAgents = extractFromNestedResponse(allAgentsResponse, 'data', 'micro_agents');
      
      if (!allMicroAgents || !Array.isArray(allMicroAgents) || allMicroAgents.length === 0) {
        throw new Error('No micro-agents found when searching for agent 104');
      }

      const microAgent104 = allMicroAgents.find(ma => 
        String(ma.micro_agent_number) === "104"
      );

      if (!microAgent104) {
        throw new Error('Micro-agent with number 104 not found');
      }

      const microAgent104Id = microAgent104.micro_agent_id;

      const payload = {
        AgentNumber: currentMicroAgentNumber,
        ParentAgent: {
          name: parentAgent.name || parentAgent.agentName,
          githubRepo: githubRepoString
        },
        ChangeInstructions: changeInstructions,
      };

      // Add ReferenceAgents if a valid reference agent is provided
      if (quickEditReferenceAgent.trim()) {
        const refAgent = quickEditReferenceAgent.trim();
        if (/^[0-9]{3}$/.test(refAgent)) {
          payload.ReferenceAgents = [refAgent];
        }
      }

      console.log('Submitting quick edit with payload:', payload);

      const quickEditResponse = await api.testAgent(
        agentId,
        'micro-agent',
        microAgent104Id,
        payload,
        'production'
      );

      if (quickEditResponse) {
        setQuickEditResult({
          success: true,
          message: 'Quick edit/fix request has been executed successfully!',
          executionArn: quickEditResponse.executionArn || quickEditResponse.body?.executionArn,
          response: quickEditResponse
        });
        
        setQuickEditInstructions('');
        setIncludeLambdaLogs(false);
        setIncludeExecutionAnalysis(false);
        setQuickEditReferenceAgent('');
        
        toast.success('Quick edit/fix executed successfully!');
        
        setTimeout(() => {
          setShowQuickEditSection(false);
          setQuickEditResult(null);
        }, 10000);
      } else {
        throw new Error('No response received from quick edit agent');
      }

    } catch (err) {
      console.error('Error submitting quick edit:', err);
      setQuickEditResult({
        success: false,
        error: err.message || 'Failed to submit quick edit request'
      });
      toast.error('Failed to submit quick edit request: ' + err.message);
    } finally {
      setSubmittingQuickEdit(false);
    }
  };

  // Fetch live logs
  const fetchLiveLogs = async (executionArn) => {
    if (!executionArn) return;

    try {
      setFetchingLogs(true);

      const agentNumber = agent?.agentNumber || '';
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error('Invalid agent number. Cannot fetch logs.');
      }

      const payload = {
        agentNumber: agentNumber,
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        executionArn: executionArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: executingEnvironment
      };

      const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/log`, payload);
      
      let rawResponse = null;
      if (response && response.response && response.response.body && response.response.body.logs) {
        rawResponse = response.response.body.logs;
      } else if (response && response.body && response.body.logs) {
        rawResponse = response.body.logs;
      } else {
        console.warn('Unexpected logs response format:', response);
        rawResponse = response;
      }
      
      const completeLogs = formatLiveLogsResponse(rawResponse);
      setLiveLogs(completeLogs);
      
      setFetchingLogs(false);
    } catch (err) {
      console.error('Error fetching live logs:', err);
      setFetchingLogs(false);
    }
  };

  // Fetch lambda logs
  const fetchLambdaLogs = async (executionArn) => {
    if (!executionArn) return;

    try {
      setFetchingLambdaLogs(true);

      const agentNumber = agent?.agentNumber || '';
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error('Invalid agent number. Cannot fetch lambda logs.');
      }

      const payload = {
        agentNumber: agentNumber,
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        executionArn: executionArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: executingEnvironment,
        AddLambdaLogs: true
      };

      console.log('Fetching lambda logs with payload:', payload);

      const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/log`, payload);
      
      console.log('Lambda logs response:', response);
      
      let lambdaLogsData = null;
      
      if (response && response.response && response.response.body) {
        let responseBody = response.response.body;
        
        if (typeof responseBody === 'string') {
          try {
            responseBody = JSON.parse(responseBody);
          } catch (parseError) {
            console.error('Error parsing lambda logs response body:', parseError);
          }
        }
        
        if (responseBody && responseBody.lambdaLogs) {
          lambdaLogsData = responseBody.lambdaLogs;
        }
      } else if (response && response.body && response.body.lambdaLogs) {
        lambdaLogsData = response.body.lambdaLogs;
      } else if (response && response.lambdaLogs) {
        lambdaLogsData = response.lambdaLogs;
      }
      
      console.log('Extracted lambda logs:', lambdaLogsData);
      setLambdaLogs(lambdaLogsData);
      
      setFetchingLambdaLogs(false);
    } catch (err) {
      console.error('Error fetching lambda logs:', err);
      setFetchingLambdaLogs(false);
    }
  };

  // Fetch execution status
  const fetchExecutionStatus = async (executionArn) => {
    if (!executionArn) return;
    
    try {
      setFetchingStatus(true);
      
      const agentNumber = agent?.agentNumber || '';
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error('Invalid agent number. Cannot fetch status.');
      }
      
      const payload = {
        agentNumber: agentNumber,
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        executionArn: executionArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: executingEnvironment
      };
      
      const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/status`, payload);
      
      if (response && response.response && response.response.body) {
        if (typeof response.response.body === 'string') {
          try {
            const statusData = JSON.parse(response.response.body);
            setExecutionStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing status response body:', parseError);
            setExecutionStatus(response.response.body);
          }
        } else {
          setExecutionStatus(response.response.body);
        }
      } else if (response && response.body) {
        if (typeof response.body === 'string') {
          try {
            const statusData = JSON.parse(response.body);
            setExecutionStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing status response body:', parseError);
            setExecutionStatus(response.body);
          }
        } else {
          setExecutionStatus(response.body);
        }
      } else {
        console.warn('Unexpected status response format:', response);
        setExecutionStatus(null);
      }
      
      setFetchingStatus(false);
    } catch (err) {
      console.error('Error fetching execution status:', err);
      setFetchingStatus(false);
    }
  };

  // Handle stop execution
  const handleStopExecution = async () => {
    if (!executionArn) return;

    try {
      setStoppingExecution(true);
      
      console.log('Stopping execution:', executionArn);
      
      const response = await api.makeAuthenticatedRequest('POST', '/execution/stop-step-function', {
        executionArn: executionArn
      });
      
      console.log('Stop execution response:', response);
      
      if (response && response.success) {
        toast.success('Execution stopped successfully');
        setStatusPollingActive(false);
        setLogsPollingActive(false);
        setTimeout(() => {
          fetchExecutionStatus(executionArn);
        }, 2000);
      } else {
        throw new Error('Failed to stop execution');
      }
    } catch (err) {
      console.error('Error stopping execution:', err);
      toast.error('Failed to stop execution: ' + err.message);
    } finally {
      setStoppingExecution(false);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (!executionArn) return;
    
    console.log('Manual refresh triggered for restored execution data');
    setFetchingLogs(true);
    setFetchingStatus(true);
    
    try {
      await Promise.all([
        fetchLiveLogs(executionArn),
        fetchExecutionStatus(executionArn)
      ]);
      
      toast.success('Logs refreshed successfully');
    } catch (err) {
      console.error('Error during manual refresh:', err);
      toast.error('Failed to refresh logs');
    } finally {
      setFetchingLogs(false);
      setFetchingStatus(false);
    }
  };

  // Handle form submission with proper error handling for 400 status
  const handleSubmit = async (env) => {
    const errors = validateForm(formFields, formData, getNestedValue);
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) return;
  
    setExecutingEnvironment(env);
    setExecuting(true);
    setError(null);
    setResult(null);
    setExecutionArn(null);
    setLiveLogs(null);
    setExecutionStatus(null);
    setExecutionDetails(null);
    setLambdaLogs(null);
    setBodyMessage(null);
    setBodyExecutionArn(null);
    setParsedExecutionSummary(null);
    setParsedAgentOutput(null);
    
    setStatusPollingActive(true);
    setLogsPollingActive(true);
    setRestoredFromLogs(false);
  
    try {
      const cleanedFormData = cleanFormDataForSubmission(formData, schema);
      
      console.log('Original form data:', formData);
      console.log('Cleaned form data for submission:', cleanedFormData);
      
      const response = await api.testAgent(agentId, agentType, targetId, cleanedFormData, env);
    
      if (response && !response.success && response.error) {
        const errorMessage = response.error.message || 'The requested step function does not exist in the backend';
        console.warn('Step function execution failed with 400:', errorMessage);
        
        setError(errorMessage);
        toast.error(`Execution failed: ${errorMessage}`);
        setExecuting(false);
        
        return;
      }

      if (response) {
        setResult(response.result || response);
        
        let parsedBodyMessage = null;
        let parsedBodyExecutionArn = null;
        
        if (response.body && typeof response.body === 'string') {
          try {
            const parsedBody = JSON.parse(response.body);
            parsedBodyMessage = parsedBody.message;
            parsedBodyExecutionArn = parsedBody.executionArn;
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
          }
        } 
        else if (response.body && typeof response.body === 'object') {
          parsedBodyMessage = response.body.message;
          parsedBodyExecutionArn = response.body.executionArn;
        }
        
        if (!parsedBodyMessage && response.message) {
          parsedBodyMessage = response.message;
        }
        if (!parsedBodyExecutionArn && response.executionArn) {
          parsedBodyExecutionArn = response.executionArn;
        }
        
        if (parsedBodyMessage) {
          setBodyMessage(safeRenderValue(parsedBodyMessage));
        }
        if (parsedBodyExecutionArn) {
          setBodyExecutionArn(safeRenderValue(parsedBodyExecutionArn));
        }

        let extractedArn = null;
      
        if (response.body && typeof response.body === 'string') {
          try {
            const parsedBody = JSON.parse(response.body);
            if (parsedBody.executionArn) {
              extractedArn = parsedBody.executionArn;
            }
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
          }
        } 
        else if (response.body && response.body.executionArn) {
          extractedArn = response.body.executionArn;
        } 
        else if (response.executionArn) {
          extractedArn = response.executionArn;
        }
      
        if (extractedArn) {
          console.log('Execution ARN extracted:', extractedArn);
          setExecutionArn(extractedArn);
          fetchLiveLogs(extractedArn);
          fetchExecutionStatus(extractedArn);
        } else {
          console.warn('Could not extract executionArn from response:', response);
        }
      
        toast.success(`Execution in ${env} initiated successfully`);
      }
    } catch (err) {
      console.error('Error executing agent:', err);
      
      if (err.response && err.response.status === 400) {
        const errorMessage = err.response.data?.error?.message || 
                           err.response.data?.message || 
                           'The requested step function does not exist in the backend';
        
        setError(errorMessage);
        toast.error(`Execution failed: ${errorMessage}`);
      } else {
        setError(`Failed to execute agent: ${err.message}`);
        toast.error('Execution failed');
      }
    } finally {
      setExecuting(false);
    }
  };

  // Navigation handlers
  const handleGoBackToAgent = () => {
    if (microAgentId) {
      navigate(`/agents/${agentId}/micro-agents/${microAgentId}`);
    } else if (miniAgentId) {
      navigate(`/agents/${agentId}/mini-agents/${miniAgentId}`);
    } else {
      navigate(`/agents/${agentId}`);
    }
  };

  const handleGoBackToParentAgent = () => {
    navigate(`/parent/${agentId}`);
  };

  const handleViewLogs = () => {
    if (microAgentId) {
      navigate(`/agents/${agentId}/micro-agents/${microAgentId}/logs`);
    } else if (miniAgentId) {
      navigate(`/agents/${agentId}/mini-agents/${miniAgentId}/logs`);
    }
  };

  // Fetch the agent schema from the db/micro-agents endpoint
  const fetchSchema = async () => {
    try {
      setLoading(true);
      console.log('Fetching schema from db/micro-agents endpoint for targetId:', targetId);
      
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1",
        micro_agent_id: targetId
      });
      
      console.log('Raw response from db/micro-agents:', response);
      
      const microAgents = extractFromNestedResponse(response, 'data', 'micro_agents');
      
      if (!microAgents || !Array.isArray(microAgents) || microAgents.length === 0) {
        throw new Error('No micro-agents found in response');
      }
      
      console.log('Found micro-agents:', microAgents.length);
      
      const microAgent = microAgents[0];
      
      if (!microAgent) {
        throw new Error(`Micro-agent with ID ${targetId} not found`);
      }
      
      console.log('Found target micro-agent:', microAgent);
      
      if (microAgent.schema) {
        let schemaObject;
        try {
          schemaObject = JSON.parse(microAgent.schema);
          console.log('Parsed schema object:', schemaObject);
        } catch (parseError) {
          console.error('Error parsing schema JSON:', parseError);
          throw new Error('Invalid schema format');
        }
        
        const inputSchema = schemaObject.input;
        
        if (inputSchema && inputSchema.properties) {
          setSchema(inputSchema);
          
          const initialData = {};
          Object.keys(inputSchema.properties).forEach(key => {
            const prop = inputSchema.properties[key];
            
            if (prop.type === 'object' && prop.properties) {
              initialData[key] = initializeNestedObject(prop.properties, prop.required);
            } else {
              initialData[key] = getDefaultValueForType(prop.type, prop);
            }
          });
          
          console.log('Initial form data with type-safe defaults:', initialData);
          
          const fields = flattenObjectToFields(
            initialData, 
            '', 
            inputSchema.properties, 
            inputSchema.required
          );
          
          setFormFields(fields);
          setFormData(initialData);
          
          console.log('Schema successfully processed. Form fields:', fields.length);
        } else {
          throw new Error('No input properties found in schema');
        }
      } else {
        throw new Error('No schema field found in micro-agent data');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError('Failed to load agent schema: ' + err.message);
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (name, value) => {
    setFormData(prev => setNestedValue(prev, name, value));
    
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Fetch agent details
  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      
      console.log(`Fetching agent details for targetId: ${targetId}`);
      
      const response = await api.getMicroAgentsbyId(targetId);

      if (response && response.microAgents && response.microAgents.length > 0) {
        const agentData = response.microAgents[0];
        
        console.log('Found agent data:', agentData);
        
        setAgent({
          id: agentData.uniqueIdentifier || targetId,
          name: agentData.microAgentName || 'Unnamed Agent',
          description: agentData.microAgentDescription || 'No description available',
          agentNumber: agentData.agentNumber || '000',
          parentAgentName: agentData.parentAgentName || 'Unknown',
          commissioned: agentData.commissioned || false,
          deployedInDev: agentData.deployedInDev || false,
          deployedInProd: agentData.deployedInProd || false
        });
        
        await fetchParentAgentDetails(agentData);
        
      } else {
        throw new Error('Agent not found in response');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching agent details:', err);
      setError('Failed to load agent details');
      setLoading(false);
    }
  };

  // Fetch parent agent details
  const fetchParentAgentDetails = async (agentData) => {
    try {
      console.log('Fetching parent agent details for:', agentData.parentAgentName);
      
      const agentsResponse = await api.getAgents();
      let parentAgentData = null;
      
      console.log('Agents response structure:', agentsResponse);
      
      let agentsList = [];
      if (agentsResponse && agentsResponse.body && agentsResponse.body.agents) {
        agentsList = agentsResponse.body.agents;
      } else if (agentsResponse && agentsResponse.agents) {
        agentsList = agentsResponse.agents;
      } else if (agentsResponse && Array.isArray(agentsResponse.data)) {
        agentsList = agentsResponse.data;
      }
      
      console.log(`Found ${agentsList.length} agents to search through`);
      
      parentAgentData = agentsList.find(a => 
        a.uniqueIdentifier === agentData.parentAgentName || 
        a.agentName === agentData.parentAgentName || 
        a.repoName === agentData.parentAgentName
      );
      
      if (!parentAgentData && agentData.parentAgentName) {
        const parentNameLower = agentData.parentAgentName.toLowerCase();
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase() === parentNameLower) ||
          (a.repoName && a.repoName.toLowerCase() === parentNameLower) ||
          (a.uniqueIdentifier && a.uniqueIdentifier.toLowerCase() === parentNameLower)
        );
      }
      
      if (!parentAgentData && agentData.parentAgentName) {
        const parentNameLower = agentData.parentAgentName.toLowerCase();
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase().includes(parentNameLower)) ||
          (a.repoName && a.repoName.toLowerCase().includes(parentNameLower))
        );
      }
      
      if (!parentAgentData) {
        console.log('No specific parent found, looking for AgentDeveloper...');
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase() === 'agentdeveloper') ||
          (a.repoName && a.repoName.toLowerCase() === 'agentdeveloper') ||
          a.uniqueIdentifier === '1'
        );
      }
      
      if (!parentAgentData && agentsList.length > 0) {
        console.log('Using first available agent as parent fallback');
        parentAgentData = agentsList[0];
      }
      
      if (parentAgentData) {
        console.log('Found parent agent data:', parentAgentData);
        
        setParentAgent({
          id: parentAgentData.uniqueIdentifier,
          name: parentAgentData.agentName || parentAgentData.repoName || agentData.parentAgentName,
          awsAccount: parentAgentData.awsAccount || parentAgentData.awsAccountNumber || "851725399969",
          awsRegion: parentAgentData.awsRegion || "us-west-2",
          repoAccount: parentAgentData.repoAccount || "materassi",
          repoName: parentAgentData.repoName || "AgentDeveloper",
          agentName: parentAgentData.agentName,
          description: parentAgentData.agentDescription || parentAgentData.description
        });
        
        console.log('Successfully set parent agent:', parentAgentData.agentName || parentAgentData.repoName);
      } else {
        console.warn('Could not find any parent agent, creating fallback');
        
        setParentAgent({
          id: "1",
          name: "AgentDeveloper",
          awsAccount: "851725399969",
          awsRegion: "us-west-2",
          repoAccount: "materassi",
          repoName: "AgentDeveloper",
          agentName: "AgentDeveloper",
          description: "Default parent agent (fallback)"
        });
        
        console.warn('Using fallback parent agent - this may cause issues with API calls');
      }
      
    } catch (err) {
      console.error('Error fetching parent agent details:', err);
      
      console.log('Error occurred, setting default parent agent');
      
      setParentAgent({
        id: "1",
        name: "AgentDeveloper",
        awsAccount: "851725399969",
        awsRegion: "us-west-2",
        repoAccount: "materassi",
        repoName: "AgentDeveloper",
        agentName: "AgentDeveloper",
        description: "Default parent agent (error fallback)"
      });
    }
  };

  // Render loading state
  if (loading && !agent) {
    return (
      <div className="agent-testing-page">
        <div className="loading-container">
          <Spinner />
          <p>Loading agent details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-testing-page">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <AgentHeader
        agent={agent}
        targetId={targetId}
        agentTypeLabel={agentTypeLabel}
        environment={environment}
        restoredFromLogs={restoredFromLogs}
        onGoBackToAgent={handleGoBackToAgent}
        onGoBackToParentAgent={handleGoBackToParentAgent}
        onViewLogs={handleViewLogs}
      />
      
      <AgentInfoCard agent={agent} parentAgent={parentAgent} />
      
      <InputParametersForm
        formFields={formFields}
        formData={formData}
        formErrors={formErrors}
        executing={executing}
        executingEnvironment={executingEnvironment}
        error={error}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
      />
      
      <ExecutionStatusCard
        executionArn={executionArn}
        bodyMessage={bodyMessage}
        bodyExecutionArn={bodyExecutionArn}
        executionStatus={executionStatus}
        fetchingStatus={fetchingStatus}
        statusPollingActive={statusPollingActive}
        fetchingLambdaLogs={fetchingLambdaLogs}
        fetchingAnalysis={fetchingAnalysis}
        stoppingExecution={stoppingExecution}
        restoredFromLogs={restoredFromLogs}
        onFetchLambdaLogs={() => fetchLambdaLogs(executionArn)}
        onAnalyzeExecution={handleAnalyzeExecution}
        onQuickEditClick={() => {
          setQuickEditInstructions('');
          setIncludeLambdaLogs(false);
          setIncludeExecutionAnalysis(false);
          setQuickEditResult(null);
          setQuickEditReferenceAgent('');
          setShowQuickEditSection(true);
        }}
        onStopExecution={handleStopExecution}
        onManualRefresh={handleManualRefresh}
      />
      
      <ExecutionResultCard
        executionStatus={executionStatus}
        parsedExecutionSummary={parsedExecutionSummary}
        parsedAgentOutput={parsedAgentOutput}
        executionDetails={executionDetails}
        loadingExecutionDetails={loadingExecutionDetails}
        restoredFromLogs={restoredFromLogs}
      />
      
      <LiveLogsCard
        executionArn={executionArn}
        restoredFromLogs={restoredFromLogs}
        liveLogs={liveLogs}
        fetchingLogs={fetchingLogs}
        logsPollingActive={logsPollingActive}
        executionStatus={executionStatus}
        statusPollingActive={statusPollingActive}
        stoppingExecution={stoppingExecution}
        agentId={agentId}
        agentType={agentType}
        onManualRefresh={handleManualRefresh}
        onStopExecution={handleStopExecution}
      />
      
      <LambdaLogsCard
        lambdaLogs={lambdaLogs}
        fetchingLambdaLogs={fetchingLambdaLogs}
      />

      <AnalysisSection
        showAnalysisSection={showAnalysisSection}
        fetchingAnalysis={fetchingAnalysis}
        analysisExecutionArn={analysisExecutionArn}
        analysisResult={analysisResult}
        analysisPollingActive={analysisPollingActive}
        fetchingAnalysisStatus={fetchingAnalysisStatus}
        executionArn={executionArn}
        onClose={() => setShowAnalysisSection(false)}
      />

      <QuickEditSection
        showQuickEditSection={showQuickEditSection}
        quickEditInstructions={quickEditInstructions}
        includeLambdaLogs={includeLambdaLogs}
        includeExecutionAnalysis={includeExecutionAnalysis}
        submittingQuickEdit={submittingQuickEdit}
        quickEditResult={quickEditResult}
        lambdaLogs={lambdaLogs}
        analysisResult={analysisResult}
        agent={agent}
        parentAgent={parentAgent}
        quickEditReferenceAgent={quickEditReferenceAgent}
        onReferenceAgentChange={setQuickEditReferenceAgent}
        onInstructionsChange={setQuickEditInstructions}
        onIncludeLambdaLogsChange={setIncludeLambdaLogs}
        onIncludeExecutionAnalysisChange={setIncludeExecutionAnalysis}
        onSubmit={handleQuickEditSubmit}
        onClose={() => {
          if (!submittingQuickEdit) {
            setShowQuickEditSection(false);
            setQuickEditResult(null);
            setQuickEditInstructions('');
            setIncludeLambdaLogs(false);
            setIncludeExecutionAnalysis(false);
            setQuickEditReferenceAgent('');
          }
        }}
      />
    </div>
  );
};

export default AgentTestingPage;